import { httpApi } from '@api/http.api.ts';
import type { UserTypes } from '@routes/___shared___/user.api';
import type { EvaluationMethod, TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import type { UserPathwayTaskStatus } from '@routes/sidebar/learning/___pages___/learning-overview/api/learningOverview.api.ts';

export const USER_PATHWAY_TASK_ACTION = {
    SaveDraft: 'SaveDraft',
    Submit: 'Submit',
    RequestExemption: 'RequestExemption',
    Recall: 'Recall',
    StartRedo: 'StartRedo',
} as const;

export const ASSESSOR_DECISION = {
    Closed: 'Closed',
    RedoRequired: 'RedoRequired',
    ExemptionGranted: 'ExemptionGranted',
    ExemptionRejected: 'ExemptionRejected',
} as const;

export type UserPathwayTaskAction = (typeof USER_PATHWAY_TASK_ACTION)[keyof typeof USER_PATHWAY_TASK_ACTION];

export type RatingPoints = 0 | 1 | 2 | 3 | 4 | 5;

type AssessorDecision = (typeof ASSESSOR_DECISION)[keyof typeof ASSESSOR_DECISION];

export interface MyLearningTaskDetailsSubtask {
    description: string;
    guideline: string;
    selfAssessmentPoints: RatingPoints | null;
    assessorRatingPoints: RatingPoints | null;
    date: string | null;
}

export interface MyLearningTaskPathwayElement {
    id: number;
    name: string;
    learningElementType: string;
    learningElementState: string;
    taskStatus: UserPathwayTaskStatus | null;
    durationInSeconds: number;
    section: string;
    tasksCount: number | null;
}

export interface MyLearningTaskDetails {
    name: string;
    pathway: string;
    rankName: string;
    vesselName: string;
    taskName: string;
    sectionName: string | null;
    subSectionName: string | null;
    subtasks: MyLearningTaskDetailsSubtask[];
    taskScaleType: TaskScaleType;
    isOrganizedInSections: boolean;
    pathwayElements: MyLearningTaskPathwayElement[];
}

export interface UserPathwayTaskAnswer {
    order: number;
    points: number;
}

export interface ElementTaskSubTask {
    description: string;
    guideline: string;
}

export interface ElementTaskConfiguration {
    taskScaleType: TaskScaleType;
    referenceText: string | null;
    subTasks: ElementTaskSubTask[];
}

export interface UserPathwayTaskAssessorFeedback {
    answers: UserPathwayTaskAnswer[];
    comments: string | null;
    evaluationMethod: EvaluationMethod | null;
    decision: AssessorDecision;
    evaluatedAtUTC: string;
}

export interface UserPathwayTaskAttempt {
    number: number;
    isCounted: boolean;
    isExemption: boolean;
    isRecalled: boolean;
    evidenceFileId: string | null;
    evidenceFileName: string | null;
    evidenceFileSize: number | null;
    assessorId: number | null;
    assessorName: string | null;
    assessorRank: string | null;
    learnerComments: string | null;
    learnerAnswers: UserPathwayTaskAnswer[];
    assessorAnswers: UserPathwayTaskAnswer[];
    assessorComments: string | null;
    evaluationMethod: EvaluationMethod | null;
    decision: string | null;
    submittedAtUTC: string;
    evaluatedAtUTC: string | null;
    recalledAtUTC: string | null;
    recallReason: string | null;
}

export interface UserPathwayTaskAssessorDelegation {
    fromAssessorId: number | null;
    toAssessorId: number;
    changedByUserId: number;
    changedAtUTC: string;
    reason: string;
}

export interface UserPathwayTaskDetailsResponse {
    userPathwayId: number;
    pathwayId: number;
    userId: number;
    pathwayItemId: number;
    status: UserPathwayTaskStatus;
    countedAttemptCount: number;
    isEvaluationStarted: boolean;
    evidenceFileId: string | null;
    evidenceFileName: string | null;
    evidenceFileSize: number | null;
    submittedAtUTC: string | null;
    assessorId: number | null;
    comments: string | null;
    assessorDraftComments: string | null;
    assessorDraftEvaluationMethod: EvaluationMethod | null;
    answers: UserPathwayTaskAnswer[];
    taskConfiguration: ElementTaskConfiguration;
    assessorFeedback: UserPathwayTaskAssessorFeedback | null;
    evaluationLog: UserPathwayTaskAttempt[];
    assessorDelegations: UserPathwayTaskAssessorDelegation[];
    details: MyLearningTaskDetails;
}

export interface UserPathwayTaskWriteRequest {
    action: UserPathwayTaskAction;
    evidenceFileId: string | null;
    assessorId: number | null;
    answers: UserPathwayTaskAnswer[] | null;
    comments: string | null;
    recallReason: string | null;
}

export interface TaskAssessorOption {
    id: number;
    fullName: string;
    rankTitle: string;
    userType: UserTypes;
}

export const getApiLearnerTask = (pathwayId: string, pathwayItemId: number): Promise<UserPathwayTaskDetailsResponse> =>
    httpApi
        .get<UserPathwayTaskDetailsResponse>(`learning/${pathwayId}/tasks/${pathwayItemId}`)
        .then(({ data }) => data);

export const putApiLearnerTask = (
    pathwayId: string,
    pathwayItemId: number,
    payload: UserPathwayTaskWriteRequest,
): Promise<UserPathwayTaskDetailsResponse> =>
    httpApi
        .put<UserPathwayTaskDetailsResponse>(`learning/${pathwayId}/tasks/${pathwayItemId}`, payload)
        .then(({ data }) => data);

export const getApiTaskAssessors = (): Promise<TaskAssessorOption[]> =>
    httpApi.get<TaskAssessorOption[]>(`learning/tasks/assessors`).then(({ data }) => data);
